import { _ } from "../../../../../core/polyglot/polyglot.js";
import {
	notBlank,
	validateAll,
	validNumber,
} from "../../../../../core/widgets/forms/validators.js";
import AbstractRuleModelVisitor from "../../../../../models/forecast/ruleModels/AbstractRuleModelVisitor.js";

/**
 * This visitor is responsible for populating a form based on the
 * visited prediction model, an instance of `AbstractPredictionRuleModel`.
 *
 * @param {formWidget} form - A form to populate with form fields
 * @param {Budget[]} budgets - A list of budgets the user can choose
 *   from to edit instances of `FromBudgetPredictionRuleModel`
 */
export default class PredictionRuleModelFormFieldsVisitor extends AbstractRuleModelVisitor {
	constructor({ form, budgets }) {
		super(...arguments);

		this._form = form;
		this._budgets = budgets;
	}

	visitConstantPredictionRuleModel(predictionModel) {
		this._form.input({
			label: _("Initial value"),
			name: "initialValue",
			validateWhileTyping: true,
			accessor: () => predictionModel.getInitialValue().toString(),
			mutator: (value) => predictionModel.setInitialValue(+value),
			validator: validateAll(validNumber({ strict: true }), notBlank()),
		});

		this._addGrowthPercentageToForm({ predictionModel });
	}

	visitFromBudgetPredictionRuleModel(predictionModel) {
		const budgets = new Map(
			this._budgets.map((budget) => [budget.getId(), budget]),
		);

		this._form.newDropDownField({
			label: _("Budget"),
			name: "budgets",
			items: [...budgets.values()].map((budget) => ({
				label: budget.getName(),
				value: budget,
			})),
			selected: budgets.get(predictionModel.getBudgetId()),
			accessor: () => budgets.get(predictionModel.getBudgetId()),
			mutator: (budget) => predictionModel.setBudgetId(budget.getId()),
		});
	}

	visitLinearRegressionPredictionRuleModel(predictionModel) {
		this._addNumberOfMonthsDropdown({
			predictionModel,
			numberOfMonthsOption: [12, 24, 36],
		});
	}

	visitSingularSpectrumAnalysisRuleModel(predictionModel) {
		this._addNumberOfMonthsDropdown({ predictionModel });
	}

	visitFromActualsPredictionRuleModel(predictionModel) {
		this._addNumberOfMonthsDropdown({ predictionModel, name: "shift" });
		this._addGrowthPercentageToForm({ predictionModel });
	}

	visitSeasonalTrendPredictionRuleModel(predictionModel) {
		this._addNumberOfMonthsDropdown({ predictionModel, name: "shift" });
	}

	visitManualEntryRuleModel(predictionModel) {
		this._form.input({
			label: _("Name"),
			name: "name",
			validateWhileTyping: true,
			accessor: () => predictionModel.getName(),
			mutator: (value) => predictionModel.setName(value),
			validator: notBlank(),
		});

		this._form.input({
			label: _("Value"),
			name: "value",
			validateWhileTyping: true,
			accessor: () => predictionModel.getValue().toString(),
			mutator: (value) => predictionModel.setValue(+value),
			validator: validateAll(validNumber({ strict: true }), notBlank()),
		});
	}

	_addNumberOfMonthsDropdown({
		predictionModel,
		numberOfMonthsOptions = [12, 24, 36, 48, 60],
		name = "numberOfMonths",
	}) {
		this._form.newDropDownField({
			label: _("Number of months"),
			name,
			items: [...numberOfMonthsOptions].map((numberOfMonths) => ({
				label: _("{numberOfMonths} months", { numberOfMonths }),
				value: numberOfMonths,
			})),
			selected: predictionModel.getShift(),
			accessor: () => predictionModel.getShift().toString(),
			mutator: (numberOfMonths) =>
				predictionModel.setShift(numberOfMonths),
		});
	}

	_addGrowthPercentageToForm({ predictionModel }) {
		this._form.input({
			label: _("Increase/Decrease (%)"),
			name: "growthPercentage",
			validateWhileTyping: true,
			accessor: () => predictionModel.getGrowthPercentage().toString(),
			mutator: (value) => predictionModel.setGrowthPercentage(+value),
			validator: validateAll(validNumber({ strict: true }), notBlank()),
		});
	}
}
